import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'

import marked from 'marked'
import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import AppBar from '@material-ui/core/AppBar'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'

import Logs from '../components/terminal/Logs'
import SSH from '../components/terminal/SSH'
import Loading from '../components/system/Loading'

import routerSelectors from '../store/selectors/router'
import launchSelectors from '../store/selectors/launch'
import messagebusActions from '../store/modules/messagebus'
import launchActions from '../store/modules/launch'
import snackbarActions from '../store/modules/snackbar'

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
  },
  stepper: {
    width: '100%',
    height: '64px',
    minHeight: '64px',
  },
  tabs: {
    width: '100%',
    height: '48px',
    minHeight: '48px',
  },
  loadingContentContainer: {
    width: '100%',
    height: 'calc(100% - 64px)',
  },
  readyContentContainer: {
    width: '100%',
    height: 'calc(100% - 48px)',
  },
  tabsPaper: {
    flexGrow: 1,
  },
  homepage: {
    padding: theme.spacing(2),
  },
  terminalContainer: {
    height: '100%',
  },
  homepagePaper: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  launchButtons: {
    flexGrow: 0,
    width: '200px',
    height: '100%',
  },
  markdown: {
    paddingLeft: theme.spacing(2),
    flexGrow: 1,
    borderLeft: '1px solid #ccc',
  },
  launchButton: {
    marginBottom: theme.spacing(2),
  }
}))

const STAGES = [{
  id: 'pool',
  title: 'Build VM Image',
}, {
  id: 'lease',
  title: 'Allocate Lease',
}, {
  id: 'vm',
  title: 'Booting VM',
}]

const Launch = ({

}) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const [ tab, setTab ] = useState(0)
  const params = useSelector(routerSelectors.params)

  const initialised = useSelector(launchSelectors.initialised)
  const stage = useSelector(launchSelectors.stage)
  const config = useSelector(launchSelectors.config)
  const pool = useSelector(launchSelectors.pool)
  const lease = useSelector(launchSelectors.lease)
  const vm = useSelector(launchSelectors.vm)
  const poolLogs = useSelector(launchSelectors.poolLogs)
  const vmLogs = useSelector(launchSelectors.vmLogs)

  const onSocketSubscribe = useCallback((channel, handler) => {
    dispatch(messagebusActions.subscribe(channel, handler))
  }, [])

  const onSocketUnsubscribe = useCallback((channel) => {
    dispatch(messagebusActions.unsubscribe(channel))
  }, [])

  const onSocketWrite  = useCallback(({
    channel,
    messageType,
    body,
  }) => {
    dispatch(messagebusActions.write({
      channel,
      messageType,
      body,
    }))
  }, [])

  const extractKubeconfigValue = useCallback((kubeconfig, name) => {
    const line = (kubeconfig || '').split("\n").find(s => s.indexOf(`##${name}=`) == 0)
    if(!line) return ''
    return line.split('=')[1]
  }, [])

  const leaseExternalIP = useMemo(() => {
    if(!lease) return ''
    return extractKubeconfigValue(lease.kubeconfig, "EXTERNAL_IP")
  }, [
    lease,
  ])

  const getButtonURL = useCallback((button) => {
    if(button.url) return button.url
    if(button.port) {
      let path = button.path || '/'
      path = path.indexOf('/') != 0 ? '/' + path : path
      const matchingPort = vm.port_mappings.app_ports.find(portMapping => portMapping.to == button.port)
      if(!matchingPort) return 'error'
      return `http://${leaseExternalIP}:${matchingPort.from}${path}`
    }
  }, [
    vm,
    leaseExternalIP,
  ])

  const onSSHError = useCallback((error) => {
    dispatch(snackbarActions.setError(error))
    setTab(0)
  }, [])

  useEffect(() => {
    dispatch(launchActions.create({
      repo: params.repo,
      file: params.file,
    }))
  }, [])

  const activeStep = useMemo(() => {
    const ret = STAGES.findIndex(s => s.id == stage)
    return ret == -1 ? STAGES.length : ret
  }, [
    stage,
  ])

  const {
    title = 'Homepage',
    buttons = [],
    homepage = '',
  } = config ? config.launch || {} : {}

  const markdown = useMemo(() => {
    if(!homepage) return ''
    return marked(homepage)
  }, [
    homepage,
  ])
  if(!initialised) return null

  return (
    <div className={ classes.root }>
      {
        stage == 'ready' ? (
          <div className={ classes.tabs }>
            <AppBar position="fixed" className={ classes.tabsPaper }>
              <Tabs value={ tab } onChange={ (e, v) => setTab(v) }>
                <Tab label={ title } />
                <Tab label="SSH" />
              </Tabs>
            </AppBar>
          </div>
        ) : (
          <div className={ classes.stepper }>
            <Stepper activeStep={ activeStep }>  
              {
                STAGES.map(stage => (
                  <Step key={ stage.id }>
                    <StepLabel>{ stage.title }</StepLabel>
                  </Step>
                ))
              }
            </Stepper>
          </div>
        )
      }
      <div className={ stage == 'ready' ? classes.readyContentContainer : classes.loadingContentContainer }>
        {
          stage == 'pool' && (
            <Logs
              logs={ poolLogs }
            />
          )
        }
        {
          stage == 'lease' && (
            <div>
              <Typography gutterBottom>waiting to allocate a lease to a vm...</Typography>
              <Loading />
            </div>
            
          )
        }
        {
          stage == 'vm' && (
            <Logs
              logs={ vmLogs }
            />
          )
        }
        {
          stage == 'ready' && tab == 0 && (
            <div className={ classes.homepage }>
              <Paper className={ classes.homepagePaper }>
                <div className={ classes.launchButtons }>
                  {
                    buttons.map((button, i) => (
                      <div className={ classes.launchButton } key={ i }>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={ () => {
                            const url = getButtonURL(button)
                            if(url == 'error') {
                              dispatch(snackbarActions.setError(`there was an error getting the button URL`))
                            }
                            else {
                              window.open(url)
                            }
                          }}
                        >
                          { button.title }
                        </Button>
                      </div>
                    ))
                  }
                </div>
                <div className={ classes.markdown }>
                  <div dangerouslySetInnerHTML={{__html: markdown}}></div>
                </div>
              </Paper>
            </div>
          )
        }
        {
          stage == 'ready' && tab == 1 && (
            <div className={ classes.terminalContainer }>
              <SSH
                pool={ pool }
                lease={ lease }
                onSubscribe={ onSocketSubscribe }
                onUnsubscribe={ onSocketUnsubscribe }
                onWrite={ onSocketWrite }
                onError={ onSSHError }
              />
            </div>
          )
        }
      </div>
    </div>
  )
}

export default Launch
